import authApi from '@/api/authApi'
import { removeToken, setToken } from '@/api/axiosClient'
import {
  INTERFACE_KEY,
  ORG_KEY,
  STATION_KEY,
  TOKEN_KEY,
} from '@/constant/common'
import { adminRoles, userRoles } from '@/constant/role'
import { CONFIG_SETUP_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import { WithChildren } from '@/types/common'
import { startTracking, stopTracking } from '@/utils/openReplay'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ADMIN_ROUTES } from '../admin/routes'
import { AuthContext } from './context'
import { useAppDispatch } from '@/store/stores'
import { setUser, clearUser } from '@/store/slices/user/userSlice'
import { fetchAllInterfaces } from '@store/slices/interface/interfaceSlice'
import { fetchInterfacesByOrganization } from '@store/slices/interfaceByOrganization/interfaceByOrganizationSlice'

// Helper functions for localStorage management
const getStoredNumber = (key: string): number | null => {
  try {
    const value = window.localStorage.getItem(key)
    if (!value || value === 'null' || value === 'undefined') return null
    const parsed = Number(value)

    return isNaN(parsed) ? null : parsed
  } catch (error) {
    console.warn(`Failed to parse localStorage value for key: ${key}`, error)
    return null
  }
}

const getStoredString = (key: string): string | null => {
  try {
    const value = window.localStorage.getItem(key)
    return value === 'null' || value === 'undefined' ? null : value
  } catch (error) {
    console.warn(`Failed to get localStorage value for key: ${key}`, error)
    return null
  }
}

const setStoredValue = (key: string, value: string | number): void => {
  try {
    window.localStorage.setItem(key, String(value))
  } catch (error) {
    console.warn(`Failed to set localStorage value for key: ${key}`, error)
  }
}

const removeStoredValue = (key: string): void => {
  try {
    window.localStorage.removeItem(key)
  } catch (error) {
    console.warn(`Failed to remove localStorage value for key: ${key}`, error)
  }
}

const AuthProvider = ({ children }: WithChildren) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Initialize authentication state
  const [isLogin, setIsLogin] = useState(() => {
    const localToken = getStoredString(TOKEN_KEY)
    if (localToken) {
      setToken(localToken)
      return true
    }
    removeToken()
    return false
  })

  // Initialize interface, org, and station IDs with proper type handling
  const [interfaceId, setInterface] = useState<number | null>(() => {
    return getStoredNumber(INTERFACE_KEY)
  })

  const [orgId, setOrg] = useState<number | null>(() => {
    return getStoredNumber(ORG_KEY)
  })

  const [stationId, setStation] = useState<number | null>(() => {
    return getStoredNumber(STATION_KEY)
  })

  const [statusInterfaceId, statusInterface] = useState<string | null>(() => {
    return getStoredString('status-interface')
  })

  const login = useCallback((email: string, password: string) => {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await authApi.login({ email, password })

        setStoredValue(TOKEN_KEY, res.data.token)
        setToken(res.data.token)

        if ([...adminRoles, ...userRoles].includes(res.data.role)) {
          setIsLogin(true)
          const { data: user } = await authApi.getProfile()
          dispatch(setUser(user))
          startTracking({
            email: user?.email || email,
            name: user?.name,
            role: user?.role,
          })

          // Hardcode redirect for specific user to Sensor Dashboard (SCRUM-826)
          if (user?.email === 'rodney_mckeown@sentosagolf.com') {
            if (user?.gmtTimeZone === null) {
              navigate(CONFIG_SETUP_ROUTES.index.to)
            } else {
              navigate(OPERATIONS_ROUTES.sensors.to)
            }
            resolve('Login success')
            return
          }

          if (adminRoles.includes(res.data.role)) {
            if (user?.gmtTimeZone === null) {
              navigate(CONFIG_SETUP_ROUTES.index.to)
            } else navigate(ADMIN_ROUTES.index.to)
          }

          if (userRoles.includes(res.data.role)) {
            if (user?.gmtTimeZone === null) {
              navigate(CONFIG_SETUP_ROUTES.index.to)
            } else navigate(OPERATIONS_ROUTES.index.to)
          }

        }

        resolve('Login success')
      } catch (error: any) {
        reject(
          new Error(error?.response?.data?.Message || 'Something went wrong!'),
        )
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const logout = useCallback(() => {
    stopTracking()

    // Clear authentication token
    removeToken()
    setIsLogin(false)

    // Clear all stored authentication data
    removeStoredValue(TOKEN_KEY)
    removeStoredValue(INTERFACE_KEY)
    removeStoredValue(ORG_KEY)
    removeStoredValue(STATION_KEY)
    removeStoredValue('status-interface')
    removeStoredValue('theme')

    // Reset state to initial values
    setInterface(null)
    setOrg(null)
    setStation(null)
    statusInterface(null)

    // Clear user from Redux store
    dispatch(clearUser())

    // Clear React Query cache
    queryClient.clear()

    // Navigate to login page
    navigate('/')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Setter functions with improved type safety and localStorage synchronization
  const setStatusInterfaceId = useCallback((status: string | null) => {
    if (status === null) {
      removeStoredValue('status-interface')
      statusInterface(null)
    } else {
      setStoredValue('status-interface', status)
      statusInterface(status)
    }
  }, [])

  const setInterfaceId = useCallback((id: number | string | null) => {
    if (id === null || id === '' || id === undefined) {
      removeStoredValue(INTERFACE_KEY)
      setInterface(null)
    } else {
      const numericId = Number(id)
      if (!isNaN(numericId)) {
        setStoredValue(INTERFACE_KEY, numericId)
        setInterface(numericId)
      }
    }
  }, [])

  const removeInterfaceId = useCallback(() => {
    removeStoredValue(INTERFACE_KEY)
    setInterface(null)
  }, [])

  const setOrgId = useCallback((id: number | string | null) => {
    if (id === null || id === '' || id === undefined) {
      removeStoredValue(ORG_KEY)
      setOrg(null)
    } else {
      const numericId = Number(id)
      if (!isNaN(numericId)) {
        setStoredValue(ORG_KEY, numericId)
        setOrg(numericId)
      }
    }
  }, [])

  const removeOrgId = useCallback(() => {
    removeStoredValue(ORG_KEY)
    setOrg(null)
  }, [])

  const setStationId = useCallback((id: number | string | null) => {
    if (id === null || id === '' || id === undefined) {
      removeStoredValue(STATION_KEY)
      setStation(null)
    } else {
      const numericId = Number(id)
      if (!isNaN(numericId)) {
        setStoredValue(STATION_KEY, numericId)
        setStation(numericId)
      }
    }
  }, [])

  const removeStationId = useCallback(() => {
    removeStoredValue(STATION_KEY)
    setStation(null)
  }, [])

  // Helper function to ensure interfaceId is valid for API calls
  const getValidInterfaceId = useCallback((): number | null => {
    return interfaceId
  }, [interfaceId])

  // Helper function to ensure orgId is valid for API calls
  const getValidOrgId = useCallback((): number | null => {
    return orgId
  }, [orgId])

  // Helper function to ensure stationId is valid for API calls
  const getValidStationId = useCallback((): number | null => {
    return stationId
  }, [stationId])

  // Effect to handle localStorage synchronization and detect external changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (!e.key) return

      switch (e.key) {
        case TOKEN_KEY:
          const newToken = e.newValue
          if (!newToken) {
            setIsLogin(false)
            removeToken()
          } else if (newToken !== getStoredString(TOKEN_KEY)) {
            setToken(newToken)
            setIsLogin(true)

          }
          break
        case INTERFACE_KEY:
          const newInterfaceId = getStoredNumber(INTERFACE_KEY)
          setInterface(newInterfaceId)
          break
        case ORG_KEY:
          const newOrgId = getStoredNumber(ORG_KEY)
          setOrg(newOrgId)
          break
        case STATION_KEY:
          const newStationId = getStoredNumber(STATION_KEY)
          setStation(newStationId)
          break
        case 'status-interface':
          const newStatusInterface = getStoredString('status-interface')
          statusInterface(newStatusInterface)
          break
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  useEffect(() => {
    if (isLogin) {
      dispatch(fetchAllInterfaces({ PageSize: 0 }))
      dispatch(fetchInterfacesByOrganization())
    }
  }, [isLogin])

  useEffect(() => {
    if (isLogin) {
      ;(async () => {
        try {
          const { data: user } = await authApi.getProfile()
          if (user?.email) {
            startTracking({
              email: user.email,
              name: user.name,
              role: user.role,
            })
          }
        } catch {
          // silently ignore - OpenReplay user identification is non-critical
        }
      })()
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        isLogin,
        login,
        logout,
        interfaceId,
        orgId,
        stationId,
        statusInterfaceId,
        setStatusInterfaceId,
        setInterfaceId,
        setOrgId,
        setStationId,
        removeInterfaceId,
        removeOrgId,
        removeStationId,
        getValidInterfaceId,
        getValidOrgId,
        getValidStationId,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
